import React, { useRef, useState } from "react";
import { styles as ui } from "stories/Admin";
import styles from "./QuranPage.module.scss";

export interface AyahAudioUrls {
  audioUrlArabic?: string;
  audioUrlNorwegian?: string;
  audioUrlUrdu?: string;
  audioUrlEnglish?: string;
}

const AUDIO_TRACKS: Array<{ key: keyof AyahAudioUrls; label: string }> = [
  { key: "audioUrlArabic", label: "Arabisk" },
  { key: "audioUrlNorwegian", label: "Norsk" },
  { key: "audioUrlUrdu", label: "Urdu" },
  { key: "audioUrlEnglish", label: "Engelsk" },
];

interface AyahAudioControlsProps {
  ayah: AyahAudioUrls;
  compact?: boolean;
}

/** Small round play/pause button around a hidden <audio> element. */
const PlayButton: React.FC<{ src: string; label: string }> = ({
  src,
  label,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => setPlaying(false));
    } else {
      audio.pause();
    }
  };

  const name = `${playing ? "Pause" : "Spill"} ${label.toLowerCase()}`;

  return (
    <>
      <button
        type="button"
        className={`${ui.btn} ${ui.ghost} ${ui.iconBtn} ${styles.playBtn} ${
          playing ? styles.playBtnOn : ""
        }`}
        onClick={toggle}
        aria-label={name}
        title={name}
      >
        <svg viewBox="0 0 24 24" width={16} height={16} aria-hidden="true">
          {playing ? (
            <path d="M8 5h3v14H8zM13 5h3v14h-3z" fill="currentColor" />
          ) : (
            <path d="M8 5v14l11-7z" fill="currentColor" />
          )}
        </svg>
        <span className={styles.playLabel}>{label.slice(0, 2)}</span>
      </button>
      <audio
        ref={audioRef}
        preload="none"
        src={src}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      >
        Nettleseren din støtter ikke lydavspilling.
      </audio>
    </>
  );
};

const AyahAudioControls: React.FC<AyahAudioControlsProps> = ({
  ayah,
  compact = false,
}) => {
  const tracks = AUDIO_TRACKS.filter((track) =>
    Boolean(ayah[track.key]?.trim()),
  );

  if (tracks.length === 0) {
    return <span className={styles.noAudio}>Ingen lyd</span>;
  }

  if (compact) {
    return (
      <span className={styles.audioCompact}>
        {tracks.map(({ key, label }) => (
          <PlayButton key={key} src={ayah[key] as string} label={label} />
        ))}
      </span>
    );
  }

  return (
    <div className={styles.audioBlock}>
      {tracks.map(({ key, label }) => (
        <div key={key} className={styles.audioTrack}>
          <span className={styles.audioLabel}>{label}</span>
          <audio
            controls
            preload="none"
            src={ayah[key]}
            className={styles.audioPlayer}
          >
            Nettleseren din støtter ikke lydavspilling.
          </audio>
        </div>
      ))}
    </div>
  );
};

export default AyahAudioControls;
